/**
 * Pillar 7 — Daily Closure Digest preview.
 *
 * Route: /digest. Project + date selector. Renders the headline +
 * counts grid + four sections (Closed Yesterday / Open / Needs
 * Attention / Plain-text body) so PM/super can review the morning
 * summary before sending.
 *
 * Resend-driven email is OFF unless env is configured. The Send button
 * is disabled with a helpful tooltip in that case.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Sunrise, Copy, Printer, Send, Loader2,
  CheckCircle2, AlertTriangle, ShieldAlert, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";
import StatusPill from "@/components/inbox/StatusPill";
import ImpactChip from "@/components/war_room/ImpactChip";


export default function DigestPreview() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(params.get("project_id") || "");
  const [forDate, setForDate] = useState(params.get("date") || "");
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState("");
  const tOpen = useRef(performance.now());
  const [openMs, setOpenMs] = useState(null);
  const hadInitialProjectIdRef = useRef(!!params.get("project_id"));

  useEffect(() => {
    let alive = true;
    api.get("/projects?archived=false").then((r) => {
      if (!alive) return;
      const ps = Array.isArray(r.data) ? r.data : r.data?.projects || [];
      setProjects(ps);
      // Mount-only default-selection: use a ref snapshot of whether a
      // project_id was present in the URL at mount, so this effect can
      // legitimately stay mount-only (no re-fetch of /projects on every
      // selection change) without omitting a reactive dependency.
      if (!hadInitialProjectIdRef.current && ps[0]) setProjectId(ps[0].id);
    }).catch(() => {});
    api.get("/health").then((r) => {
      if (!alive) return;
      const cfg = !!(r.data?.checks?.email_configured ?? r.data?.email_configured);
      setEmailConfigured(cfg);
    }).catch(() => setEmailConfigured(false));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    const tStart = performance.now();
    api.get(`/projects/${projectId}/digest${forDate ? `?for_date=${encodeURIComponent(forDate)}` : ""}`)
      .then((r) => {
        if (!alive) return;
        setDigest(r.data);
        if (openMs == null) setOpenMs(Math.round(performance.now() - tOpen.current));
      })
      .catch((e) => {
        if (alive) confirm.raw?.error?.(e?.response?.data?.detail || "Couldn't build digest.");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, forDate]); // eslint-disable-line

  const copySummary = async () => {
    if (!digest?.plain_text) return;
    await navigator.clipboard.writeText(digest.plain_text);
    confirm.raw?.success?.("Summary copied to clipboard");
  };

  const sendNow = async () => {
    const list = recipients.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) {
      confirm.raw?.error?.("Add at least one recipient email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/projects/${projectId}/digest/send`, { recipients: list });
      confirm.raw?.success?.(`Send queued · ${r.data?.send_status || ""}`);
    } catch (e) {
      confirm.raw?.error?.(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--fop-bg)]" data-testid="digest-preview">
      <header className="border-b border-[var(--fop-border)] bg-[var(--fop-surface)] px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <Sunrise size={20} className="text-[var(--fop-primary)]" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
            Daily Closure Digest
          </div>
          <div className="text-sm font-semibold text-[var(--fop-text)]">
            Yesterday closed · still open · needs attention today
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <select
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setParams({ project_id: e.target.value, ...(forDate ? { date: forDate } : {}) }); }}
            data-testid="digest-project-select"
            className="px-2 py-1 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[12px] text-[var(--fop-text)]"
          >
            <option value="">Pick a project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.number || ""})
              </option>
            ))}
          </select>
          <input
            type="date"
            value={forDate ? forDate.slice(0, 10) : ""}
            onChange={(e) => { const v = e.target.value; setForDate(v); setParams({ project_id: projectId, ...(v ? { date: v } : {}) }); }}
            data-testid="digest-date"
            className="px-2 py-1 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[12px] text-[var(--fop-text)]"
          />
          {openMs != null && (
            <span className="text-[10px] font-mono text-[var(--fop-text-3)]" data-testid="digest-perf"
                  title={`Digest opened in ${openMs}ms`}>
              open {openMs}ms
            </span>
          )}
        </div>
      </header>

      <div className="p-4 max-w-5xl mx-auto">
        {loading && !digest ? (
          <div className="p-8 text-sm text-[var(--fop-text-3)] flex items-center gap-2" data-testid="digest-loading">
            <Loader2 className="animate-spin" size={14} /> Building digest…
          </div>
        ) : !digest ? (
          <div className="p-8 text-sm text-[var(--fop-text-3)]">Pick a project to preview the digest.</div>
        ) : (
          <>
            {/* Headline */}
            <div className="mb-4 p-4 rounded border border-[var(--fop-primary)]/45 bg-[var(--fop-primary-soft)]"
                 data-testid="digest-headline">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-primary)] mb-1">
                {digest.project_name}
              </div>
              <div className="text-base font-semibold text-[var(--fop-text)]">
                {digest.headline}
              </div>
            </div>

            {/* Counts grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5" data-testid="digest-counts-grid">
              <Counter label="Closed yesterday" value={digest.counts.closed_yesterday} icon={CheckCircle2} tone="success" testid="digest-count-closed" />
              <Counter label="Acknowledged yesterday" value={digest.counts.acknowledged_yesterday} icon={CheckCircle2} tone="muted" testid="digest-count-ack" />
              <Counter label="Open clarifications" value={digest.counts.open_clarifications} icon={AlertTriangle} tone="warning" testid="digest-count-clarifications" />
              <Counter label="Open conflicts" value={digest.counts.open_conflicts} icon={ShieldAlert} tone="danger" testid="digest-count-conflicts" />
              <Counter label="Overdue items" value={digest.counts.overdue_items} icon={Clock} tone="danger" testid="digest-count-overdue" />
              <Counter label="Critical or high open" value={digest.counts.critical_open} icon={AlertTriangle} tone="danger" testid="digest-count-critical" />
              <Counter label="Foremen pending" value={digest.counts.foremen_pending} icon={AlertTriangle} tone="warning" testid="digest-count-pending" />
            </div>

            {/* Three columns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
              <Column title="Closed yesterday" items={digest.closed_yesterday} testid="digest-col-closed" />
              <Column title="Still open" items={[...(digest.open_conflicts || []), ...(digest.open_clarifications || []), ...(digest.overdue_items || [])]} testid="digest-col-open" />
              <Column title="Needs attention today" items={digest.critical_open} testid="digest-col-attention" />
            </div>

            {/* Plain-text + actions */}
            <div className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden">
              <div className="px-3 py-1.5 border-b border-[var(--fop-border)] flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
                  Print / Email-ready summary
                </span>
                <button onClick={copySummary} data-testid="digest-copy"
                        className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">
                  <Copy size={11} /> Copy
                </button>
                <button onClick={() => window.print()} data-testid="digest-print"
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-text)]">
                  <Printer size={11} /> Print
                </button>
              </div>
              <pre
                className="p-3 text-[12px] text-[var(--fop-text-2)] whitespace-pre-wrap font-mono"
                data-testid="digest-plain-text"
              >
                {digest.plain_text}
              </pre>
              <div className="px-3 py-2 border-t border-[var(--fop-border)] flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  placeholder="email1@x, email2@y"
                  disabled={!emailConfigured}
                  data-testid="digest-recipients"
                  className="flex-1 min-w-[200px] px-2 py-1 rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[12px] text-[var(--fop-text)]"
                />
                <button
                  onClick={sendNow}
                  disabled={!emailConfigured || sending}
                  data-testid="digest-send"
                  className="btn btn-primary text-[12px] inline-flex items-center gap-1 disabled:opacity-50"
                  title={emailConfigured ? "Send digest to recipients" : "Email provider not configured (RESEND_API_KEY + EMAIL_FROM)"}
                >
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Send digest
                </button>
                {!emailConfigured && (
                  <span className="text-[10px] font-mono text-[var(--fop-text-3)]">
                    · Email send disabled — provider not configured
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function Counter({ label, value, icon: Icon, tone, testid }) {
  const border = tone === "danger" ? "border-[var(--fop-danger,#dc2626)]/45"
                 : tone === "warning" ? "border-[var(--fop-warning)]/55"
                 : tone === "success" ? "border-[var(--fop-success)]/55"
                 : "border-[var(--fop-border)]";
  return (
    <div className={`p-2.5 rounded border ${border} bg-[var(--fop-surface)]`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
        <Icon size={11} /> {label}
      </div>
      <div className="text-lg font-semibold text-[var(--fop-text)] mt-1">{value || 0}</div>
    </div>
  );
}


function Column({ title, items, testid }) {
  return (
    <div className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden" data-testid={testid}>
      <div className="px-3 py-1.5 border-b border-[var(--fop-border)] font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
        {title}
      </div>
      {(items || []).length === 0 ? (
        <div className="p-3 text-[12px] text-[var(--fop-text-3)]">Nothing here.</div>
      ) : (
        <ul className="divide-y divide-[var(--fop-border)]">
          {(items || []).slice(0, 8).map((it) => (
            <li key={it.id} className="px-3 py-2 text-[12px] flex items-start gap-2">
              <StatusPill status={(it.completed_at ? "complete" : it.state) || "new"} />
              <div className="flex-1 min-w-0">
                <div className="text-[var(--fop-text)] truncate font-medium">{it.title || `${it.sheet_number} REV ${it.current_revision}`}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">
                  {it.sheet_number} · REV {it.current_revision}
                </div>
              </div>
              <ImpactChip severity={it.priority || "moderate"} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
